import { Card, Box, Divider, CircularProgress, Typography } from '@material-ui/core'
import ChatMessageList from './ChatMessageList'
import ChatMessageInput from './ChatMessageInput'
import { useState, useEffect, useMemo } from 'react'
import useAuth from 'src/hooks/useAuth'
import { useManageUser } from 'src/api/user/manage'

const NoteCard = ({ row, saveMessage }) => {
  const [tabPage, setTabPage] = useState(0)
  const { user } = useAuth()
  const { self, updateMutation } = useManageUser(user)
  const userDetails = self.data

  // Local state for optimistic updates
  const [localMessages, setLocalMessages] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Server data from props
  const serverData = row.note_list || { messages: [] }

  // Initialize and sync with server data when it changes
  useEffect(() => {
    setLocalMessages(serverData.messages || [])
  }, [JSON.stringify(serverData.messages)]) // Use JSON.stringify for deep comparison

  // Combined data for display (local messages take precedence)
  const displayData = useMemo(
    () => ({
      ...serverData,
      messages: localMessages,
    }),
    [serverData, localMessages],
  )

  const handleSendMessage = async ({ message }) => {
    const newMessage = {
      body: message,
      createdAt: new Date().toISOString(),
      sender: ' ', // Space character as requested
    }

    // Optimistic update - show message immediately
    const updatedMessages = [...localMessages, newMessage]
    setLocalMessages(updatedMessages)
    setIsSubmitting(true)

    const updatedData = {
      ...serverData,
      messages: updatedMessages,
    }

    try {
      // Save to server
      await saveMessage(updatedData)
      // The parent component will refresh and sync the data
    } catch (error) {
      console.error('Failed to save message:', error)
      // Revert optimistic update on error
      setLocalMessages(localMessages)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ marginLeft: 1, borderBottom: 1, borderColor: 'divider' }}>
        <ChatMessageList conversation={displayData} />
      </Box>
      <Divider />
      <Box sx={{ padding: 1, position: 'relative' }}>
        <ChatMessageInput onSend={handleSendMessage} tabIndex={tabPage} disabled={isSubmitting} />
        {isSubmitting && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              right: 16,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption">Saving...</Typography>
          </Box>
        )}
      </Box>
    </Card>
  )
}

export default NoteCard
